import React, { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import styles from './Kyc.module.css';

export type Option = { value: string; label: string; hint?: string; search?: string };

const PHONE = '(max-width: 640px)';
const LIST_H = 320;

/**
 * A dropdown you can type into: tap to open, type to narrow the list, pick
 * with a tap or ↑ ↓ Enter. The list floats over the page (never cut off by a
 * card), opens upward when there's no room below, and is a bottom sheet on a
 * phone. `allowCustom` lets the customer use what they typed when it isn't in
 * the list (a new district, an area the post office doesn't name).
 */
const SearchSelect: React.FC<{
  value: string;
  options: Option[];
  onChange: (value: string) => void;
  placeholder: string;
  searchPlaceholder?: string;
  disabled?: boolean;
  allowCustom?: boolean;
  /** Shown in the closed box instead of the option's label (e.g. "🇸🇦 +966"). */
  display?: (o: Option) => React.ReactNode;
  className?: string;
  ariaLabel?: string;
  onBlur?: () => void;
}> = ({ value, options, onChange, placeholder, searchPlaceholder = 'Search…', disabled, allowCustom, display, className, ariaLabel, onBlur }) => {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState('');
  const [active, setActive] = useState(0);
  const [place, setPlace] = useState<React.CSSProperties>({});
  const [phone, setPhone] = useState(false);
  const box = useRef<HTMLButtonElement>(null);
  const pop = useRef<HTMLDivElement>(null);
  const list = useRef<HTMLUListElement>(null);
  const search = useRef<HTMLInputElement>(null);

  const picked = options.find((o) => o.value === value);
  const typed = q.trim();
  const shown = useMemo(() => {
    const t = typed.toLowerCase();
    const hits = t ? options.filter((o) => `${o.label} ${o.hint ?? ''} ${o.search ?? ''}`.toLowerCase().includes(t)) : options;
    const exact = hits.some((o) => o.label.toLowerCase() === t);
    return allowCustom && t && !exact ? [...hits, { value: typed, label: `Use “${typed}”`, search: typed }] : hits;
  }, [typed, options, allowCustom]);

  // Where the list goes: under the box, above it if there's no room, or a bottom sheet on a phone.
  const measure = () => {
    const isPhone = window.matchMedia(PHONE).matches;
    setPhone(isPhone);
    if (isPhone || !box.current) return setPlace({});
    const r = box.current.getBoundingClientRect();
    const below = window.innerHeight - r.bottom;
    const up = below < LIST_H + 16 && r.top > below;
    setPlace({
      position: 'fixed',
      left: r.left,
      width: Math.max(r.width, 260),
      ...(up ? { bottom: window.innerHeight - r.top + 6 } : { top: r.bottom + 6 }),
    });
  };

  useLayoutEffect(() => {
    if (open) measure();
  }, [open]);

  useEffect(() => {
    if (!open) return;
    setQ('');
    setActive(Math.max(0, options.findIndex((o) => o.value === value)));
    setTimeout(() => search.current?.focus(), 0);
    const away = (e: Event) => {
      const t = e.target as Node;
      if (!box.current?.contains(t) && !pop.current?.contains(t)) close();
    };
    const follow = () => measure();
    document.addEventListener('mousedown', away);
    document.addEventListener('touchstart', away);
    window.addEventListener('resize', follow);
    window.addEventListener('scroll', follow, true);
    return () => {
      document.removeEventListener('mousedown', away);
      document.removeEventListener('touchstart', away);
      window.removeEventListener('resize', follow);
      window.removeEventListener('scroll', follow, true);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  useEffect(() => {
    list.current?.querySelector<HTMLElement>(`[data-i="${active}"]`)?.scrollIntoView({ block: 'nearest' });
  }, [active, open]);

  function close() {
    setOpen(false);
    onBlur?.();
  }

  function choose(o: Option) {
    onChange(o.value);
    close();
    box.current?.focus();
  }

  function onKey(e: React.KeyboardEvent) {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setActive((i) => Math.min(i + 1, shown.length - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setActive((i) => Math.max(i - 1, 0));
    } else if (e.key === 'Enter') {
      e.preventDefault();
      if (shown[active]) choose(shown[active]);
    } else if (e.key === 'Escape') {
      e.preventDefault();
      close();
      box.current?.focus();
    }
  }

  const label = picked ? (display ? display(picked) : picked.label) : value || placeholder;

  return (
    <div className={`${styles.ss} ${className ?? ''}`}>
      <button
        ref={box}
        type="button"
        className={styles.ssBox}
        disabled={disabled}
        onClick={(e) => {
          e.preventDefault();
          setOpen((v) => !v);
        }}
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-label={ariaLabel}
      >
        <span className={picked || value ? '' : styles.ssPh}>{label}</span>
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2.4} aria-hidden>
          <path d="M6 9l6 6 6-6" />
        </svg>
      </button>
      {open &&
        createPortal(
          <>
            {phone && <div className={styles.ssScrim} onClick={close} />}
            <div ref={pop} className={`${styles.ssPop} ${phone ? styles.ssSheet : ''}`} style={place} role="dialog" aria-label={ariaLabel}>
              {phone && (
                <div className={styles.ssSheetHead}>
                  <b>{ariaLabel ?? placeholder}</b>
                  <button type="button" onClick={close} aria-label="Close">✕</button>
                </div>
              )}
              <div className={styles.ssSearch}>
                <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2.2} aria-hidden>
                  <circle cx="11" cy="11" r="7" />
                  <path d="M21 21l-4.3-4.3" />
                </svg>
                <input
                  ref={search}
                  value={q}
                  onChange={(e) => {
                    setQ(e.target.value);
                    setActive(0);
                  }}
                  onKeyDown={onKey}
                  placeholder={searchPlaceholder}
                />
              </div>
              <ul ref={list} role="listbox" className={styles.ssList}>
                {shown.length === 0 && <li className={styles.ssNone}>{allowCustom ? 'Type the name' : 'No match'}</li>}
                {shown.map((o, i) => (
                  <li
                    key={`${o.value}-${i}`}
                    data-i={i}
                    role="option"
                    aria-selected={o.value === value}
                    className={`${styles.ssItem} ${i === active ? styles.ssActive : ''} ${o.value === value ? styles.ssPicked : ''}`}
                    onMouseEnter={() => setActive(i)}
                    onMouseDown={(e) => e.preventDefault()}
                    onClick={() => choose(o)}
                  >
                    <span>{o.label}</span>
                    {o.hint && <small>{o.hint}</small>}
                    {o.value === value && <b aria-hidden>✓</b>}
                  </li>
                ))}
              </ul>
            </div>
          </>,
          document.body,
        )}
    </div>
  );
};

export default SearchSelect;
